import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { HeartHandshake, Sparkles, ShieldCheck, Heart, Loader2, ExternalLink } from "lucide-react";

const PRESETS = [25, 50, 100];

export default function Donations() {
  const [plan, setPlan] = useState("50");
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [portalEmail, setPortalEmail] = useState("");
  const [portalLoading, setPortalLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [polling, setPolling] = useState(false);
  const [thanks, setThanks] = useState(null);

  // Handle return from Stripe Checkout
  useEffect(() => {
    if (!location.pathname.startsWith("/doacoes/sucesso")) return;
    const params = new URLSearchParams(location.search);
    // MP volta com ?ref=<external_reference>; Stripe volta com ?session_id=<id>
    const ref = params.get("ref");
    const session = params.get("session_id");
    if (!ref && !session) return;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const url = ref
          ? `/donations/subscription/mp-status/${ref}`
          : `/donations/subscription/status/${session}`;
        const { data } = await api.get(url);
        // MP: status "authorized" = cartão validado e assinatura ativa
        // Stripe: payment_status "paid"
        const done = data.status === "authorized" || data.payment_status === "paid";
        if (done) {
          setPolling(false);
          setThanks({ amount: data.amount_brl, plan: data.plan });
          return;
        }
        if (data.status === "expired" || data.status === "cancelled" || attempts >= 10) {
          setPolling(false);
          toast.warning("Pagamento em processamento. Você receberá uma confirmação em instantes.");
          navigate("/doacoes", { replace: true });
          return;
        }
        setTimeout(poll, 2000);
      } catch (_) {
        setPolling(false);
        toast.error("Falha ao verificar pagamento.");
      }
    };
    poll();
  }, [location.pathname, location.search, navigate]);

  const finalAmount = plan === "custom" ? Number(custom) : Number(plan);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Preencha nome e e-mail."); return; }
    if (!finalAmount || finalAmount < 5) { toast.error("Valor mínimo R$ 5."); return; }
    setLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/donations/subscribe", {
        plan, custom_amount: plan === "custom" ? finalAmount : null,
        name: form.name, email: form.email, phone: form.phone, origin,
      });
      // Abre o checkout em nova aba/navegador externo — exigência da Apple
      // Store: doações precisam acontecer FORA do webview do app.
      if (data.url) {
        const win = window.open(data.url, "_blank", "noopener,noreferrer");
        if (!win) {
          // Fallback quando o browser bloqueia o popup (ex: iOS webview restrito):
          // redireciona a aba atual — o próprio Mercado Pago será aberto e o
          // sistema decide se abre Safari externo.
          window.location.href = data.url;
        } else {
          toast.success("Abrimos o Mercado Pago em uma nova aba para você.");
          setLoading(false);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao iniciar cobrança.");
      setLoading(false);
    }
  };

  const openPortal = async (e) => {
    e.preventDefault();
    if (!portalEmail) { toast.error("Informe o e-mail cadastrado."); return; }
    setPortalLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/donations/portal", { email: portalEmail, origin });
      if (data.url) {
        // Portal do assinante também abre externamente.
        const win = window.open(data.url, "_blank", "noopener,noreferrer");
        if (!win) window.location.href = data.url;
        else setPortalLoading(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não encontramos essa assinatura.");
      setPortalLoading(false);
    }
  };

  if (polling) {
    return (
      <div className="container-cuidar py-24 max-w-md mx-auto text-center">
        <Loader2 className="h-10 w-10 animate-spin mx-auto text-[hsl(var(--primary))]" />
        <div className="mt-4 font-heading font-bold">Confirmando sua doação…</div>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Isso leva poucos segundos.</p>
      </div>
    );
  }

  if (thanks) {
    return (
      <div className="container-cuidar py-20 max-w-2xl">
        <div className="surface p-10 text-center">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[hsl(var(--secondary))]/20 text-[hsl(var(--secondary))]">
            <HeartHandshake className="h-8 w-8" />
          </div>
          <h1 className="mt-4 font-heading text-3xl font-black">Doação ativada!</h1>
          <p className="mt-3 text-[hsl(var(--muted-foreground))]">
            Sua doação mensal recorrente de <b>R$ {Number(thanks.amount).toFixed(2)}</b> foi confirmada e o Stripe cobrará seu cartão automaticamente todo mês. Você acabou de fazer parte da rede que cuida de quem cuida. 💚
          </p>
          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">
            Enviamos um agradecimento pelo WhatsApp e você receberá um relatório mensal do impacto do seu apoio.
          </p>
          <Button onClick={() => navigate("/")} className="mt-6 rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="thanks-home-btn">
            Voltar ao início
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="relative overflow-hidden border-b border-[hsl(var(--border))] bg-gradient-to-br from-[hsl(var(--secondary))]/10 via-white to-[hsl(var(--primary))]/8">
        <div className="container-cuidar py-14">
          <div className="text-xs font-bold uppercase tracking-widest text-[hsl(var(--secondary))] inline-flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> Doação mensal recorrente
          </div>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-black leading-tight max-w-3xl">
            Cuide de quem cuida — <span className="text-[hsl(var(--secondary))]">todo mês</span>
          </h1>
          <p className="mt-5 max-w-2xl text-[hsl(var(--muted-foreground))]">
            Sua contribuição mensal cobre terapias, materiais e a equipe que orienta famílias diariamente.
            Cobrança automática no cartão de crédito via Stripe — cancele ou altere quando quiser.
          </p>
        </div>
      </section>

      <section className="container-cuidar py-12 grid gap-10 lg:grid-cols-[1.2fr_1fr]">
        <form onSubmit={subscribe} className="surface p-6 sm:p-8 space-y-6" data-testid="subscribe-form">
          <div>
            <div className="font-heading text-lg font-black">Escolha um valor mensal</div>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {PRESETS.map((v) => (
                <button type="button" key={v} onClick={() => { setPlan(String(v)); setCustom(""); }}
                  className={`rounded-2xl border-2 p-5 text-left transition-all ${
                    plan === String(v)
                      ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/6 shadow-md -translate-y-0.5"
                      : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/40"
                  }`}
                  data-testid={`plan-${v}`}>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">R$ / mês</div>
                  <div className="font-heading text-3xl font-black text-[hsl(var(--primary))]">R$ {v}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                    {v === 25 && "Toda ajuda conta"}
                    {v === 50 && "Mais popular"}
                    {v === 100 && "Padrinho / madrinha"}
                  </div>
                </button>
              ))}
            </div>
            <div className={`mt-3 rounded-2xl border-2 p-5 transition-all ${plan === "custom" ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/6" : "border-[hsl(var(--border))]"}`}>
              <label className="flex items-center gap-3">
                <input type="radio" checked={plan === "custom"} onChange={() => setPlan("custom")} data-testid="plan-custom-radio" />
                <span className="font-heading font-bold">Outro valor</span>
              </label>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-sm font-bold text-[hsl(var(--muted-foreground))]">R$</span>
                <Input type="number" min="5" max="5000" step="1" value={custom}
                  onChange={(e) => { setCustom(e.target.value); setPlan("custom"); }}
                  onFocus={() => setPlan("custom")}
                  placeholder="Ex: 75" className="max-w-[160px]" data-testid="plan-custom-input" />
                <span className="text-sm text-[hsl(var(--muted-foreground))]">/ mês</span>
              </div>
            </div>
          </div>

          <div className="border-t border-[hsl(var(--border))] pt-6 space-y-4">
            <div className="font-heading text-lg font-black">Seus dados</div>
            <div>
              <Label htmlFor="d-name">Nome completo</Label>
              <Input id="d-name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required data-testid="donor-name-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="d-email">E-mail</Label>
                <Input id="d-email" type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} required data-testid="donor-email-input" />
              </div>
              <div>
                <Label htmlFor="d-phone">WhatsApp</Label>
                <Input id="d-phone" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} placeholder="(11) 99999-9999" data-testid="donor-phone-input" />
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-[hsl(var(--border))]">
            <Button type="submit" disabled={loading} className="btn-pill h-14 w-full sm:w-auto sm:px-10 bg-[hsl(var(--secondary))] text-white hover:bg-[hsl(var(--secondary))]/90 font-bold text-base shadow-md" data-testid="subscribe-btn">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Abrindo Stripe…</> : <><Heart className="h-5 w-5 mr-2 fill-white" /> Doar R$ {finalAmount || 0} / mês</>}
            </Button>
            <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" /> Cobrança processada com segurança pelo Stripe · Cancele quando quiser
            </div>
          </div>
        </form>

        <aside className="space-y-5">
          <div className="surface p-6">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Como funciona</div>
            <ul className="mt-3 space-y-3 text-sm">
              <li className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5 shrink-0" /> Você cadastra o cartão uma vez no Stripe.</li>
              <li className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5 shrink-0" /> Todo mês o valor é debitado automaticamente.</li>
              <li className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5 shrink-0" /> Recebe agradecimento e relatório de impacto via WhatsApp.</li>
              <li className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5 shrink-0" /> Pode alterar valor ou cancelar a qualquer momento.</li>
            </ul>
          </div>

          <div className="surface p-6 bg-[hsl(var(--muted))]/40">
            <div className="font-heading font-bold flex items-center gap-2"><ExternalLink className="h-4 w-4" /> Já é doador?</div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Acesse o portal Stripe para alterar valor, trocar cartão ou cancelar sua assinatura.</p>
            <form onSubmit={openPortal} className="mt-3 flex gap-2">
              <Input value={portalEmail} onChange={(e) => setPortalEmail(e.target.value)} type="email" placeholder="E-mail cadastrado" data-testid="portal-email-input" />
              <Button type="submit" disabled={portalLoading} variant="outline" className="rounded-full shrink-0" data-testid="portal-open-btn">
                {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Abrir"}
              </Button>
            </form>
          </div>

          <div className="surface p-6 bg-[hsl(var(--primary))]/6 border-[hsl(var(--primary))]/20">
            <div className="font-heading font-bold">Prefere doar uma única vez?</div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              Fale com nosso time pelo WhatsApp para orientarmos como fazer uma doação avulsa via Pix ou transferência.
            </p>
          </div>
        </aside>
      </section>
    </div>
  );
}
